import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

function Dashboard() {
  const { user, isPoolSupervisor } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [guides, setGuides] = useState([]);
  const [geoStatus, setGeoStatus] = useState([]);
  const [extras, setExtras] = useState([]);
  const [swimmingBookings, setSwimmingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExtrasForm, setShowExtrasForm] = useState(false);
  const [extraForm, setExtraForm] = useState({ guide_id: '', amount: '', description: '', booking_ref: '' });
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    if (isPoolSupervisor) {
      const data = await api.getSwimmingBookings(today);
      if (data?.bookings) setSwimmingBookings(data.bookings);
      else if (Array.isArray(data)) setSwimmingBookings(data);
      else setSwimmingBookings([]);
    } else if (user.role === 'admin') {
      const [groupsData, geoData, guidesData, extrasData] = await Promise.all([
        api.getGroups(today), api.geoStatus(), api.getGuides(), api.getExtras(today)
      ]);
      if (groupsData?.groups) setGroups(groupsData.groups);
      if (geoData?.active_checkins) setGeoStatus(geoData.active_checkins);
      else if (geoData?.checkins) setGeoStatus(geoData.checkins);
      if (guidesData?.guides) setGuides(guidesData.guides);
      if (extrasData?.extras) setExtras(extrasData.extras);
    } else {
      // Guide: load my groups + all groups summary
      const [myData, allData] = await Promise.all([
        api.getMyGroups(today), api.getGroups(today)
      ]);
      if (myData?.groups) setGroups(myData.groups);
      if (allData?.groups) setAllGroups(allData.groups);
    }
    setLoading(false);
  };

  const markAttendance = async (bookingId, status) => {
    let reason = '';
    if (status === 'no_show') {
      reason = window.prompt('Raison du no-show:');
      if (reason === null) return;
    }
    await api.reportAttendance(bookingId, status, reason);
    loadData();
  };

  const handleAddExtra = async () => {
    if (!extraForm.guide_id || !extraForm.amount) return;
    await api.addExtra(extraForm.guide_id, extraForm.amount, extraForm.description, extraForm.booking_ref, today);
    setExtraForm({ guide_id: '', amount: '', description: '', booking_ref: '' });
    setShowExtrasForm(false);
    loadData();
  };

  const handleDeleteExtra = async (id) => {
    if (!window.confirm('Supprimer?')) return;
    await api.deleteExtra(id);
    loadData();
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) return <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>;

  // ==================== POOL SUPERVISOR DASHBOARD ====================
  if (isPoolSupervisor) {
    return (
      <div className="animate-in">
        <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '20px', marginBottom: '12px', border: '1px solid #333' }}>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>🏊 Réservations Piscine</div>
          <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>📅 {formatDate(today)}</div>
        </div>

        {swimmingBookings.length === 0 ? (
          <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '30px', textAlign: 'center', border: '1px solid #333' }}>
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>🏊</div>
            <div style={{ color: '#888' }}>Aucune réservation piscine aujourd'hui</div>
          </div>
        ) : (
          swimmingBookings.map((booking, index) => (
            <div key={booking.id || index} style={{ background: '#1a1a1a', borderRadius: '16px', padding: '16px', marginBottom: '12px', borderLeft: '4px solid #3B82F6', border: '1px solid #333' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '15px', color: '#fff' }}>{booking.first_name}</div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                    👥 {booking.participants} participant{booking.participants > 1 ? 's' : ''}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                    📍 {booking.meeting_point_name || 'N/A'}
                  </div>
                  {booking.time_slot && (
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                      🕐 {booking.time_slot}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ background: '#1E3A5F', color: '#60A5FA', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, marginBottom: '6px' }}>
                    {booking.swimming_option || 'Swimming'}
                  </div>
                  {booking.guide_name ? (
                    <div style={{ fontSize: '12px', color: '#10B981', fontWeight: 500 }}>{booking.guide_name}</div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#F59E0B', fontWeight: 500 }}>Non assigné</div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  }

  // ==================== ADMIN DASHBOARD ====================
  if (user.role === 'admin') {
    const totalP = groups.reduce((s, g) => s + (g.total_participants || 0), 0);
    const assigned = groups.filter(g => g.guide_id).length;
    const guidesList = guides.filter(g => g.role === 'guide');
    const guidesWithGroups = [...new Set(groups.filter(g => g.guide_id).map(g => g.guide_id))];
    const guidesWithout = guidesList.filter(g => !guidesWithGroups.includes(g.id));
    const extrasTotal = extras.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const pickupAlerts = {};
    geoStatus.forEach(c => { if (!pickupAlerts[c.pickup_point]) pickupAlerts[c.pickup_point] = []; pickupAlerts[c.pickup_point].push(c.guide_name); });

    return (
      <div className="animate-in">
        <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '20px', marginBottom: '12px', border: '1px solid #333' }}>
          <div style={{ fontSize: '13px', color: '#888' }}>📅 {formatDate(today)}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px', marginTop: '14px' }}>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: '24px', fontWeight: 800, color: '#E53E3E' }}>{groups.length}</div><div style={{ fontSize: '10px', color: '#888' }}>Groupes</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: '24px', fontWeight: 800, color: '#3B82F6' }}>{totalP}</div><div style={{ fontSize: '10px', color: '#888' }}>Clients</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: '24px', fontWeight: 800, color: '#10B981' }}>{guidesWithGroups.length}</div><div style={{ fontSize: '10px', color: '#888' }}>Actifs</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: '24px', fontWeight: 800, color: '#F59E0B' }}>{guidesWithout.length}</div><div style={{ fontSize: '10px', color: '#888' }}>Libres</div></div>
          </div>
        </div>

        {guidesWithout.length > 0 && (
          <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '14px', marginBottom: '12px', border: '1px solid #333' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#F59E0B' }}>⚠️ Guides sans groupe ({guidesWithout.length})</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {guidesWithout.map(g => (
                <a key={g.id} href={`https://wa.me/${g.phone?.replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer"
                  style={{ background: '#2a2a2a', border: '1px solid #444', borderRadius: '8px', padding: '4px 10px', fontSize: '11px', color: '#ccc', textDecoration: 'none' }}>{g.name} 💬</a>
              ))}
            </div>
          </div>
        )}

        <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '14px', marginBottom: '12px', border: '1px solid #333' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>📍 Points de rencontre</div>
          {Object.keys(pickupAlerts).length > 0 ? Object.entries(pickupAlerts).map(([point, names]) => (
            <div key={point} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #2a2a2a' }}>
              <div className="geo-pulse"></div>
              <span style={{ fontSize: '12px', fontWeight: 500 }}>{point}</span>
              <span style={{ fontSize: '11px', color: '#10B981', marginLeft: 'auto' }}>{names.join(', ')}</span>
            </div>
          )) : <div style={{ color: '#555', fontSize: '12px' }}>Aucun guide aux points</div>}
          {geoStatus.length > 0 && <button onClick={() => navigate('/map')} style={{ width: '100%', marginTop: '10px', padding: '8px', background: '#2a2a2a', border: '1px solid #444', borderRadius: '8px', color: '#60A5FA', fontSize: '12px', cursor: 'pointer' }}>🗺 Carte</button>}
        </div>

        <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '14px', marginBottom: '12px', border: '1px solid #333' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>💰 Extras</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {extrasTotal > 0 && <span style={{ fontSize: '13px', fontWeight: 700, color: '#10B981' }}>{extrasTotal} MAD</span>}
              <button onClick={() => setShowExtrasForm(!showExtrasForm)} style={{ background: '#E53E3E', border: 'none', color: 'white', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>+</button>
            </div>
          </div>
          {showExtrasForm && (
            <div style={{ background: '#2a2a2a', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
              <select value={extraForm.guide_id} onChange={e => setExtraForm({...extraForm, guide_id: e.target.value})} style={{ width: '100%', padding: '8px', background: '#3a3a3a', border: '1px solid #555', borderRadius: '6px', color: 'white', fontSize: '12px', marginBottom: '6px' }}>
                <option value="">-- Guide --</option>
                {guidesList.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                <input type="number" placeholder="MAD" value={extraForm.amount} onChange={e => setExtraForm({...extraForm, amount: e.target.value})} style={{ flex: 1, padding: '8px', background: '#3a3a3a', border: '1px solid #555', borderRadius: '6px', color: 'white', fontSize: '12px' }} />
                <input type="text" placeholder="Ref" value={extraForm.booking_ref} onChange={e => setExtraForm({...extraForm, booking_ref: e.target.value})} style={{ flex: 1, padding: '8px', background: '#3a3a3a', border: '1px solid #555', borderRadius: '6px', color: 'white', fontSize: '12px' }} />
              </div>
              <input type="text" placeholder="Description" value={extraForm.description} onChange={e => setExtraForm({...extraForm, description: e.target.value})} style={{ width: '100%', padding: '8px', background: '#3a3a3a', border: '1px solid #555', borderRadius: '6px', color: 'white', fontSize: '12px', marginBottom: '6px' }} />
              <button onClick={handleAddExtra} style={{ width: '100%', padding: '8px', background: '#10B981', border: 'none', borderRadius: '6px', color: 'white', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Enregistrer</button>
            </div>
          )}
          {extras.length > 0 ? extras.map(e => (
            <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #2a2a2a' }}>
              <span style={{ fontSize: '12px' }}>{e.guide_name} · {e.description || 'Extra'}</span>
              <div style={{ display: 'flex', gap: '6px' }}><span style={{ fontSize: '12px', color: '#10B981', fontWeight: 700 }}>{e.amount} MAD</span><button onClick={() => handleDeleteExtra(e.id)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}>✗</button></div>
            </div>
          )) : <div style={{ fontSize: '11px', color: '#555' }}>Aucun extra</div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <button onClick={() => navigate('/groups')} style={{ background: '#E53E3E', color: 'white', border: 'none', borderRadius: '12px', padding: '14px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>📋 Groupes</button>
          <button onClick={() => navigate('/search')} style={{ background: '#2a2a2a', color: 'white', border: '1px solid #444', borderRadius: '12px', padding: '14px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>🔍 Rechercher</button>
        </div>
      </div>
    );
  }

  // ==================== GUIDE DASHBOARD ====================
  return (
    <div className="animate-in">
      {/* Header */}
      <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '20px', marginBottom: '12px', border: '1px solid #333' }}>
        <div style={{ fontSize: '18px', fontWeight: 700 }}>👋 Bonjour {user.name}</div>
        <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>{formatDate(today)}</div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          <div style={{ background: '#2a2a2a', borderRadius: '10px', padding: '8px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#E53E3E' }}>{groups.length}</div>
            <div style={{ fontSize: '10px', color: '#888' }}>Mes groupes</div>
          </div>
          <div style={{ background: '#2a2a2a', borderRadius: '10px', padding: '8px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#3B82F6' }}>{groups.reduce((s,g) => s + (g.total_participants||0), 0)}</div>
            <div style={{ fontSize: '10px', color: '#888' }}>Mes clients</div>
          </div>
          <div style={{ background: '#2a2a2a', borderRadius: '10px', padding: '8px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#F59E0B' }}>{allGroups.length}</div>
            <div style={{ fontSize: '10px', color: '#888' }}>Total groupes</div>
          </div>
        </div>
      </div>

      {/* My Groups (with full details) */}
      {groups.length === 0 ? (
        <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '30px', textAlign: 'center', border: '1px solid #333' }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>📭</div>
          <div style={{ color: '#888' }}>Aucun groupe assigné aujourd'hui</div>
        </div>
      ) : (
        groups.map(group => (
          <div key={group.id} style={{ background: '#1a1a1a', borderRadius: '16px', padding: '16px', marginBottom: '12px', borderLeft: '4px solid #E53E3E', border: '1px solid #333' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '16px' }}>🕐 {group.time_slot?.substring(0, 5)}</div>
                <div style={{ fontSize: '13px', color: '#888' }}>📍 {group.pickup_point}</div>
              </div>
              <div style={{ background: '#1E3A5F', color: '#60A5FA', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>{group.total_participants}P</div>
            </div>
            <div style={{ marginTop: '12px' }}>
              {(group.bookings || []).map(b => (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #333' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '14px' }}>{b.first_name} {b.surname}</div>
                    <div style={{ fontSize: '11px', color: '#888' }}>{b.participants}P · {b.language} · {b.reference}</div>
                    {b.notes && <div style={{ fontSize: '10px', color: '#F59E0B' }}>📝 {b.notes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {b.phone && b.phone !== '***' && (
                      <a href={`https://wa.me/${b.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer"
                        style={{ background: '#25D366', color: 'white', border: 'none', borderRadius: '8px', padding: '6px 8px', fontSize: '11px', textDecoration: 'none' }}>WA</a>
                    )}
                    <button onClick={() => markAttendance(b.id, 'present')} style={{ background: '#065F46', color: '#6EE7B7', border: 'none', borderRadius: '8px', padding: '6px 8px', fontSize: '11px', cursor: 'pointer' }}>✓</button>
                    <button onClick={() => markAttendance(b.id, 'no_show')} style={{ background: '#7F1D1D', color: '#FCA5A5', border: 'none', borderRadius: '8px', padding: '6px 8px', fontSize: '11px', cursor: 'pointer' }}>✗</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* All Groups Summary (no booking details, just guide names for coordination) */}
      {allGroups.length > 0 && (
        <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '14px', marginTop: '16px', border: '1px solid #333' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>👥 Tous les groupes du jour ({allGroups.length})</div>
          {allGroups.map((g, i) => (
            <div key={g.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < allGroups.length - 1 ? '1px solid #2a2a2a' : 'none' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 500 }}>
                  🕐 {g.time_slot?.substring(0,5)} · 📍 {g.pickup_point}
                </div>
                <div style={{ fontSize: '11px', color: '#888' }}>{g.total_participants}P</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {g.guide_name ? (
                  <span style={{ fontSize: '11px', color: '#10B981', fontWeight: 500 }}>{g.guide_name}</span>
                ) : (
                  <span style={{ fontSize: '11px', color: '#F59E0B' }}>Non assigné</span>
                )}
                {g.guide_id && guides.find(gd => gd.id === g.guide_id)?.phone && (
                  <a href={`https://wa.me/${(guides.find(gd => gd.id === g.guide_id)?.phone || '').replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer"
                    style={{ background: '#25D366', color: 'white', borderRadius: '6px', padding: '2px 6px', fontSize: '9px', textDecoration: 'none' }}>💬</a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
